import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import { TranslocoService } from '@jsverse/transloco';
import { cloneDeep } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class NavigationMockApi {
    private readonly _compactNavigation: FuseNavigationItem[] = [
        {
            id: 'home',
            icon: 'heroicons_outline:home',
            title: 'fidelity-app.navigation.home',
            type: 'basic',
            link: 'fidelity-app/home',
            classes: { wrapper: 'menu-item-col', icon: 'm-0' },
        },
        {
            id: 'fidelity',
            icon: 'heroicons_outline:credit-card',
            title: 'fidelity-app.navigation.fidelity',
            type: 'basic',
            link: 'fidelity-app/fidelity',
            classes: { wrapper: 'menu-item-col', icon: 'm-0' },
        },
        {
            id: 'coupons',
            icon: 'heroicons_outline:ticket',
            title: 'fidelity-app.navigation.coupons',
            type: 'basic',
            link: 'fidelity-app/coupons',
            classes: { wrapper: 'menu-item-col', icon: 'm-0' },
        },
        {
            id: 'store-locator',
            icon: 'heroicons_outline:map-pin',
            title: 'fidelity-app.navigation.shops',
            type: 'basic',
            link: 'fidelity-app/store-locator',
            classes: { wrapper: 'menu-item-col', icon: 'm-0' },
        },
        {
            id: 'more',
            icon: 'heroicons_outline:ellipsis-horizontal',
            title: 'fidelity-app.navigation.more',
            type: 'basic',
            link: 'fidelity-app/more',
            classes: { wrapper: 'menu-item-col', icon: 'm-0' },
        },
    ];
    private readonly _defaultNavigation: FuseNavigationItem[] = [
        {
            id: 'home',
            icon: 'heroicons_outline:home',
            title: 'fidelity-app.navigation.home',
            type: 'basic',
            link: 'fidelity-app/home',
        },
        {
            id: 'fidelity',
            icon: 'heroicons_outline:credit-card',
            title: 'fidelity-app.navigation.fidelity',
            type: 'basic',
            link: 'fidelity-app/fidelity',
        },
        {
            id: 'coupons',
            icon: 'heroicons_outline:ticket',
            title: 'fidelity-app.navigation.coupons',
            type: 'basic',
            link: 'fidelity-app/coupons',
        },
        {
            id: 'store-locator',
            icon: 'heroicons_outline:map-pin',
            title: 'fidelity-app.navigation.shops',
            type: 'basic',
            link: 'fidelity-app/store-locator',
        },
        {
            id: 'more',
            icon: 'heroicons_outline:ellipsis-horizontal',
            title: 'fidelity-app.navigation.more',
            type: 'basic',
            link: 'fidelity-app/more',
        },
    ];

    /**
     * Constructor
     */
    constructor(
        private _fuseMockApiService: FuseMockApiService,
        private _translocoService: TranslocoService,
        private _destroyRef: DestroyRef
    ) {
        // Register Mock API handlers
        this.loadLangAndRegisterHandlers();

        // Subscribe to language changes and refresh navigation data
        // DO NOT REMOVE THIS! Sometimes the navigation items are registered before the language is loaded,
        // so we need to ensure that the navigation items are translated correctly.
        this._translocoService.langChanges$.pipe(takeUntilDestroyed()).subscribe(() => {
            // Force refresh of navigation data by re-registering handlers
            this.loadLangAndRegisterHandlers();
        });
    }

    private loadLangAndRegisterHandlers() {
        // Make sure the translations are actually loaded before registering
        this._translocoService
            .load(this._translocoService.getActiveLang())
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => this.registerHandlers());
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
    get defaultNavigation() {
        return this._defaultNavigation;
    }

    get compactNavigation() {
        return this._compactNavigation;
    }

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ Navigation - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService.onGet('api/common/navigation').reply(() => {
            // Fill compact navigation children using the default navigation
            this._compactNavigation.forEach((compactNavItem) => {
                this._defaultNavigation.forEach((defaultNavItem) => {
                    if (defaultNavItem.id === compactNavItem.id) {
                        compactNavItem.children = cloneDeep(defaultNavItem.children);
                    }
                });
            });

            // Return the response
            return [
                200,
                {
                    compact: this._compactNavigation.map((a) => {
                        return {
                            ...a,
                            title: this._translocoService.translate(a.title),
                            children: a.children?.map((c) => {
                                return {
                                    ...c,
                                    title: this._translocoService.translate(c.title),
                                };
                            }),
                        };
                    }),
                    default: this._defaultNavigation.map((a) => {
                        return {
                            ...a,
                            title: this._translocoService.translate(a.title),
                            children: a.children?.map((c) => {
                                return {
                                    ...c,
                                    title: this._translocoService.translate(c.title),
                                };
                            }),
                        };
                    }),
                },
            ];
        });
    }
}
